import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Icon, type IconName } from '@/components/icons';
import { Avatar, type AvatarGender } from '@/components/ui';

interface ProfileCardProps {
  open: boolean;
  onClose: () => void;
  name?: string;
  email?: string;
  gender?: AvatarGender | null;
  roleName?: string;
  institutionName?: string;
  groupName?: string;
}

/** Accounts web — where the person manages their own account. */
const ACCOUNTS_WEB_URL =
  import.meta.env.VITE_ACCOUNTS_WEB_URL || 'https://accounts.campusone.cskytelecoms.com';

function DetailRow({ icon, label, value }: { icon: IconName; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <Icon name={icon} size={18} aria-hidden="true" className="mt-0.5 text-slate-400" />
      <div className="min-w-0">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
        <div className="truncate text-sm text-slate-700">{value}</div>
      </div>
    </div>
  );
}

export function ProfileCard({ open, onClose, name, email, gender, roleName, institutionName, groupName }: ProfileCardProps) {
  const { t } = useTranslation();
  const seed = encodeURIComponent(name?.trim() || email?.trim() || 'CampusOne');
  const coverSrc = `https://picsum.photos/seed/${seed}/640/240`;
  const organization = institutionName ?? groupName;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title=""
      hideHeader
      className="w-[22rem] max-w-full overflow-hidden"
      bodyClassName="p-0"
    >
      <div className="relative">
        <img src={coverSrc} alt="" className="h-40 w-full object-cover" />
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="absolute right-3 top-3 rounded-full bg-black/40 p-1.5 text-white transition-colors hover:bg-black/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <Icon name="close" size={16} aria-hidden="true" />
        </button>
      </div>

      <div className="px-6 pb-6">
        <div className="-mt-10 mb-3 flex items-end justify-between">
          <Avatar name={name} gender={gender} size={84} className="bg-white shadow-md ring-4 ring-white" />
          {roleName && (
            <span className="mb-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              {roleName}
            </span>
          )}
        </div>

        <h3 className="truncate text-lg font-bold text-slate-800">{name || t('common.account')}</h3>
        {organization && <p className="truncate text-sm text-slate-500">{organization}</p>}

        <div className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
          {email && <DetailRow icon="mail" label={t('profile.email')} value={email} />}
          {roleName && <DetailRow icon="role" label={t('profile.role')} value={roleName} />}
          {organization && <DetailRow icon="building" label={t('profile.institution')} value={organization} />}
        </div>

        <button
          type="button"
          onClick={() => window.open(ACCOUNTS_WEB_URL, '_blank', 'noopener,noreferrer')}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--sapBrandColor)] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sapBrandColor)]"
        >
          {t('profile.manageAccount')}
          <Icon name="external" size={14} aria-hidden="true" />
        </button>
      </div>
    </Modal>
  );
}